/**
 * An address of an element.
 */
export interface Address {
  /**
   * The chapter index (1-based).
   */
  chapter: number;
  /**
   * The article index (1-based).
   */
  article?: number;
}

/**
 * An interface for a displayable element such as
 * a chapter or an article.
 */
export interface Displayable {
  /**
   * True if the element should be displayed, false otherwise.
   */
  display: boolean;
  /**
   * The id of the element.
   */
  address: Address;

}

/**
 * A part of the document that contains a heading and a sub-heading.
 */
export interface Part {
  /**
   * The heading.
   */
  h1: string;
  /**
   * The sub-heading.
   */
  h2: string;
}

/**
 * Represents the top-level element of a document.
 * A chapter may have either a set of sections or
 * a set of articles. Not both.
 */
export interface Chapter extends Part, Displayable {
  /**
   * The sections.
   */
  sec: [Section];
  /**
   * The articles.
   */
  art: [Article];
}

/**
 * Represents a section that further breaks down
 * chapters with lots of articles into smaller
 * groups.
 */
export interface Section extends Part {
  /**
   * The articles.
   */
  art: [Article];
}

/**
 * The individual articles with consist of
 * paragraphs that may be organized hierarchically.
 */
export interface Article extends Part, Displayable {
  /**
   * The paragraphs of the article.
   */
  p: [Paragraph];
}

/**
 * A paragraph that may contain (hierarchically
 * organized sub-paragraphs). It will always
 * contain a text.
 */
export interface Paragraph {
  /**
   * The text of the article. This is plain text,
   * but we encode superscript using _..._ e.g.
   * _superscript value_ and italics using *...*,
   * e.g. *italic value*.
   */
  t: string;
  /**
   * A number of sub paragraphs.
   */
  p: [Paragraph];
}

export function HtmlEncode(text: string): string {
  const el = document.createElement('div');
  el.innerText = el.textContent = text;
  text = el.innerHTML;
  let italic = false;
  let superscript = false;
  for (let i = text.length - 1; i >= 0; i--) {
    if (text.charAt(i) === '_') {
      text = text.substr(0, i) + (superscript ? '<sup>' : '</sup>') + text.substr(i + 1);
      superscript = !superscript;
    } else if (text.charAt(i) === '*') {
      text = text.substr(0, i) + (italic ? '<i>' : '</i>') + text.substr(i + 1);
      italic = !italic;
    }
  }
  return text;
}
